import type { V2Call, V2Coin } from "./action";

export type PrivacyConsentRequest = (
  kind: string,
  title: string,
  disclosure: string
) => Promise<boolean>;

export type PrivacyConsent = {
  kind: string;
  title: string;
  disclosure: string;
};

export const MAX_RELAYER_FEE_BPS = 1_000n;
const FEE_DENOMINATOR = 10_000n;
const MAX_U128 = (1n << 128n) - 1n;
const MAX_BANK_BALANCE = (1n << 256n) - 1n;
// A strict end assertion also rejects a final newline (JS `$` alone does not).
const NATIVE_DENOM = /^[A-Za-z][A-Za-z0-9/:._-]{2,127}(?![\s\S])/;

// Read only from the attested contract configuration, never relayer metadata.
export const parseCallRelayerFeeCaps = (value: unknown): V2Coin[] => {
  if (value === undefined) return [];
  if (!Array.isArray(value) || value.length > 16) {
    throw new Error("Invalid private call fee limits");
  }
  let previous = "";
  return value.map((coin: unknown) => {
    if (!coin || typeof coin !== "object" || Array.isArray(coin)) {
      throw new Error("Invalid private call fee limit");
    }
    const { denom, amount } = coin as Record<string, unknown>;
    if (
      typeof denom !== "string" ||
      !NATIVE_DENOM.test(denom) ||
      denom <= previous ||
      typeof amount !== "string" ||
      amount.length > 39 ||
      !/^[1-9]\d*(?![\s\S])/.test(amount) ||
      BigInt(amount) > MAX_U128
    ) {
      throw new Error("Invalid private call fee limit");
    }
    previous = denom;
    return { denom, amount };
  });
};

export const protocolFeeCoins = (
  totals: ReadonlyMap<string, bigint>,
  feeBps: number,
  rounding: "floor" | "ceil" = "floor"
): V2Coin[] => {
  if (!Number.isSafeInteger(feeBps) || feeBps < 0 || feeBps > 10_000) {
    throw new Error("Invalid protocol fee policy");
  }
  return [...totals]
    .map(([denom, amount]) => ({
      denom,
      amount: (
        (amount * BigInt(feeBps) +
          (rounding === "ceil" ? FEE_DENOMINATOR - 1n : 0n)) /
        FEE_DENOMINATOR
      ).toString(),
    }))
    .filter((coin) => BigInt(coin.amount) > 0n);
};

export const assertRelayerFeeWithinLimit = (
  fundedAmount: bigint,
  feeAmount: bigint
): void => {
  if (
    fundedAmount < 0n ||
    feeAmount < 0n ||
    feeAmount > (fundedAmount * MAX_RELAYER_FEE_BPS) / FEE_DENOMINATOR
  ) {
    throw new Error("Relayer fee exceeds the 10% safety limit");
  }
};

export const privateCallTotals = (
  calls: readonly V2Call[]
): ReadonlyMap<string, bigint> => {
  const totals = new Map<string, bigint>();
  const add = (coin: V2Coin) => {
    const amount = BigInt(coin.amount);
    if (amount < 0n) throw new Error("Private call amount cannot be negative");
    totals.set(coin.denom, (totals.get(coin.denom) ?? 0n) + amount);
  };
  for (const call of calls) {
    if ("bank_send" in call) call.bank_send.amount.forEach(add);
    else if ("wasm_execute" in call) call.wasm_execute.funds.forEach(add);
    else
      add({
        denom: call.thorchain_deposit.denom,
        amount: call.thorchain_deposit.amount,
      });
  }
  return totals;
};

export const assertPrivateCallRelayerFee = (
  calls: readonly V2Call[],
  fee: V2Coin,
  configuredCaps: readonly V2Coin[] = []
): void => {
  if (
    !NATIVE_DENOM.test(fee.denom) ||
    fee.amount.length > 39 ||
    !/^[1-9]\d*(?![\s\S])/.test(fee.amount) ||
    BigInt(fee.amount) > MAX_U128
  ) {
    throw new Error("Invalid private relayer fee");
  }
  const cap = parseCallRelayerFeeCaps(configuredCaps).find(
    (coin) => coin.denom === fee.denom
  );
  if (cap) {
    if (BigInt(fee.amount) > BigInt(cap.amount)) {
      throw new Error("Relayer fee exceeds the on-chain private call limit");
    }
    return;
  }
  const fundedAmount = privateCallTotals(calls).get(fee.denom) ?? 0n;
  assertRelayerFeeWithinLimit(fundedAmount, BigInt(fee.amount));
};

export const selectPrivateCallRelayerFee = async (params: {
  calls: readonly V2Call[];
  minimumFees: ReadonlyMap<string, bigint>;
  sponsored: boolean;
  configuredCaps: readonly V2Coin[];
  balance: (denom: string) => Promise<bigint>;
}): Promise<V2Coin | undefined> => {
  // Validate the contract policy even when a relayer sponsors this action.
  const caps = parseCallRelayerFeeCaps(params.configuredCaps);
  if (params.sponsored) return undefined;
  if (params.minimumFees.size > 16)
    throw new Error("Invalid relayer fee policy");
  const candidates = [...params.minimumFees].sort(([a], [b]) =>
    a === "rune" ? -1 : b === "rune" ? 1 : a < b ? -1 : a > b ? 1 : 0
  );
  let permitted = false;
  for (const [denom, amount] of candidates) {
    const fee = { denom, amount: amount.toString() };
    try {
      assertPrivateCallRelayerFee(params.calls, fee, caps);
    } catch {
      continue;
    }
    permitted = true;
    // This checks pre-funded fee liquidity only. The relayer's full simulation
    // remains authoritative for product calls, intermediate receipts and fees.
    const available = await params.balance(denom);
    if (available < 0n || available > MAX_BANK_BALANCE) {
      throw new Error("Invalid private account fee balance");
    }
    if (available >= amount) return fee;
  }
  throw new Error(
    permitted
      ? "Insufficient private account balance for the relayer fee. Move fee funds from Private Reserve to Spending and retry. No public-wallet transaction was submitted."
      : "The relayer's fee is not permitted by this contract. Use a compatible or sponsored relayer. No public-wallet transaction was submitted."
  );
};

export const rejectPrivateCallSelfSubmission = (): never => {
  throw new Error(
    "Private relaying is unavailable for this action. Retry when a compatible relayer is available. No public-wallet transaction was submitted."
  );
};

export const confirmRelayerFee = async (
  request: PrivacyConsentRequest,
  fee: V2Coin | undefined,
  source: "private-operation" | "spending-account" = "private-operation"
): Promise<void> => {
  if (!fee || BigInt(fee.amount) === 0n) return;
  await requirePrivacyConsent(
    request,
    {
      kind: "private-operation-relayer-fee",
      title: "Confirm relayer fee",
      disclosure:
        source === "spending-account"
          ? `Your selected private Spending account will pay the relayer exactly ${fee.amount} ${fee.denom} (base units), separately from the product amount.`
          : `The relayer will receive exactly ${fee.amount} ${fee.denom} (base units) from this private operation.`,
    },
    new Error("Relayer fee declined")
  );
};

export const confirmProtocolFees = async (
  request: PrivacyConsentRequest,
  fees: readonly V2Coin[],
  mode: "deducted" | "additional"
): Promise<void> => {
  if (fees.length === 0) return;
  const amounts = fees.map((fee) => `${fee.amount} ${fee.denom}`).join(", ");
  await requirePrivacyConsent(
    request,
    {
      kind: "private-operation-protocol-fee",
      title: "Confirm protocol fee",
      disclosure:
        mode === "additional"
          ? `The private account will pay an additional protocol fee of ${amounts} (base units). The product call itself remains unchanged.`
          : `The private reserve transfer includes a protocol fee of ${amounts} (base units), deducted from the requested transfer amount.`,
    },
    new Error("Protocol fee declined")
  );
};

export const requirePrivacyConsent = async (
  request: PrivacyConsentRequest,
  consent: PrivacyConsent,
  declined: unknown
): Promise<void> => {
  const accepted = await request(
    consent.kind,
    consent.title,
    consent.disclosure
  );
  if (!accepted) throw declined;
};

export const ensureRecoveryExportComplete = (
  required: boolean,
  operation: string
): void => {
  if (required) {
    throw new Error(
      `Download and confirm the encrypted recovery package before ${operation}`
    );
  }
};
